import { useState, useEffect, useCallback, useRef } from 'react';
import {
  inventoryProduct as inventoryProductApi,
  invoices as invoicesApi
} from 'utilities/api';
import catchCancel from 'helpers/catchCancel';
import formatInvoiceDateParam from 'screens/Inventory/helpers/formatInvoiceDateParam';

const EMPTY_SEASON_IDS = [];

const useInventoryInvoiceData = (
  prodId,
  cropSeasonIds = EMPTY_SEASON_IDS,
  useProductScopedLookup = false,
  sortBy = undefined,
  sortDir = undefined,
  dateRange = undefined
) => {
  const requestIdRef = useRef(0);
  const cropSeasonIdsParam = cropSeasonIds.join(',');
  const scopedCropSeasonIdsParam = useProductScopedLookup
    ? cropSeasonIdsParam
    : undefined;
  const scopedSortBy = useProductScopedLookup ? sortBy : undefined;
  const scopedSortDir = useProductScopedLookup ? sortDir : undefined;
  const hasCompleteDateRange = Boolean(dateRange?.[0] && dateRange?.[1]);
  const scopedStartDate =
    useProductScopedLookup && hasCompleteDateRange
      ? formatInvoiceDateParam(dateRange?.[0], false)
      : undefined;
  const scopedEndDate =
    useProductScopedLookup && hasCompleteDateRange
      ? formatInvoiceDateParam(dateRange?.[1], true)
      : undefined;
  const requestKey = JSON.stringify([
    prodId,
    scopedCropSeasonIdsParam,
    scopedSortBy,
    scopedSortDir,
    scopedStartDate,
    scopedEndDate,
    useProductScopedLookup
  ]);
  const [requestState, setRequestState] = useState({
    requestKey,
    invoices: [],
    loading: true,
    error: null
  });

  const getAllInvoices = useCallback(
    (productId, requestId, currentRequestKey) => {
      const sortParams = new URLSearchParams({
        ...(scopedCropSeasonIdsParam
          ? { cropSeasonIds: scopedCropSeasonIdsParam }
          : {}),
        ...(scopedSortBy ? { sortBy: scopedSortBy } : {}),
        ...(scopedSortDir ? { sortDir: scopedSortDir } : {}),
        ...(scopedStartDate ? { startDate: scopedStartDate } : {}),
        ...(scopedEndDate ? { endDate: scopedEndDate } : {})
      });
      const queryString = sortParams.toString();
      const request = useProductScopedLookup
        ? inventoryProductApi
            .createChildApi({
              action: `product/${productId}/invoices${
                queryString ? `?${queryString}` : ''
              }`
            })
            .fetch(null)
        : invoicesApi.fetch(null, { size: 10000 });
      const { promise } = request;
      promise
        .then(data => {
          if (requestId !== requestIdRef.current) return;
          const { results } = data.data;
          setRequestState({
            requestKey: currentRequestKey,
            invoices: useProductScopedLookup
              ? results
              : results.filter(invoice =>
                  (invoice.invoiceProducts ?? []).some(
                    product =>
                      product.productId === productId ||
                      product.id === productId
                  )
                ),
            loading: false,
            error: null
          });
        })
        .catch(err => {
          if (requestId !== requestIdRef.current) return;
          setRequestState({
            requestKey: currentRequestKey,
            invoices: [],
            loading: false,
            error: err
          });
        })
        .catch(catchCancel);
    },
    [
      scopedCropSeasonIdsParam,
      scopedSortBy,
      scopedSortDir,
      scopedStartDate,
      scopedEndDate,
      useProductScopedLookup
    ]
  );
  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!prodId) {
      setRequestState({
        requestKey,
        invoices: [],
        loading: false,
        error: null
      });
    } else {
      getAllInvoices(prodId, requestId, requestKey);
    }

    return () => {
      requestIdRef.current += 1;
    };
  }, [prodId, getAllInvoices, requestKey]);

  const isCurrentRequest = requestState.requestKey === requestKey;
  return {
    invoices: isCurrentRequest ? requestState.invoices : [],
    invsLoading: !isCurrentRequest || requestState.loading,
    invsError: isCurrentRequest ? requestState.error : null
  };
};

export default useInventoryInvoiceData;
